import { useAddressFinder } from '@/composables/useAddressFinder'
import { useClient } from '@/composables/useClient'
import { HttpError } from '@/queries/auth-error'
import { entityKeys } from '@/queries/entity-keys'
import { mutationOptions } from '@/queries/mutation-options'
import type { Address, PatchUser, PaymentMethod, PutDefaultAddress, User } from '@repo/api-types'
import { addressToString } from '@/utils/addressToString'
import { isSameLocality } from '@/utils/addressVerification'
import type { AddressInput } from '@/schemas/address'
import { queryOptions } from '@tanstack/vue-query'

const accountKeys = {
  profile: () => [...entityKeys.account(), 'profile'] as const,
  defaultAddress: () => [...entityKeys.address()] as const,
  addressVerification: (query: string) => [...entityKeys.address(), 'verification', query] as const,
  defaultPaymentMethod: () => [...entityKeys.payment()] as const,
  emailTaken: (email: string) => [...entityKeys.account(), 'email-taken', email] as const,
}

export const accountQueries = {
  profile: () =>
    queryOptions({
      queryKey: accountKeys.profile(),
      queryFn: async (): Promise<User> => {
        const client = await useClient()
        const { status, body } = await client.auth.getMe()

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to fetch user profile')
      },
    }),

  defaultAddress: () =>
    queryOptions({
      queryKey: accountKeys.defaultAddress(),
      queryFn: async (): Promise<Address> => {
        const client = await useClient()
        const { status, body } = await client.auth.getDefaultCustomerAddress()

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to fetch default address')
      },
    }),

  /**
   * AddressFinder check (shared with CCA, see utils/addressVerification): `true` when the top
   * AddressFinder result for the address is in the same suburb + postcode. Throws if the lookup itself fails.
   */
  addressVerification: (address?: Pick<AddressInput, 'address1' | 'address2' | 'suburb' | 'state' | 'postcode'>) => {
    const query = addressToString(address)

    return queryOptions({
      queryKey: accountKeys.addressVerification(query),
      enabled: Boolean(query),
      queryFn: async (): Promise<boolean> => {
        const addressFinder = useAddressFinder()
        const search = await addressFinder.addressSearch(query)

        if (!search.success) throw new Error('Address lookup failed')

        const topSearchedResult = search.completions[0]

        if (!topSearchedResult) return false

        const { data } = await addressFinder.addressInfo(topSearchedResult.id)
        
        if (!data?.postCode) throw new Error('Address lookup failed')

        return isSameLocality(
          { suburb: address!.suburb, postcode: address!.postcode },
          { suburb: data.suburb, postcode: data.postCode },
        )
      },
      staleTime: Infinity,
      retry: false,
    })
  },

  defaultPaymentMethod: () =>
    queryOptions({
      queryKey: accountKeys.defaultPaymentMethod(),
      queryFn: async (): Promise<PaymentMethod> => {
        const client = await useClient()
        const { status, body } = await client.payment.getDefaultPaymentMethod()

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to fetch default payment method')
      },
    }),

  emailTaken: (email: string) =>
    queryOptions({
      queryKey: accountKeys.emailTaken(email),
      queryFn: async (): Promise<{ isTaken: boolean }> => {
        const client = await useClient()
        const { status, body } = await client.auth.isEmailTaken({ query: { email } })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to check email availability')
      },
    }),
}

export const accountMutations = {
  updateProfile: () =>
    mutationOptions({
      mutationFn: async (user: PatchUser): Promise<User> => {
        const client = await useClient()
        const { status, body } = await client.auth.patchMe({ body: user })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to update account details')
      },
      invalidation: { awaited: [{ entity: 'account' }] },
    }),

  updatePassword: () =>
    mutationOptions({
      mutationFn: async ({ newPassword }: { newPassword: string }): Promise<{ success: boolean }> => {
        const client = await useClient()
        const { status, body } = await client.auth.updatePassword({ body: { newPassword } })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to update password')
      },
      invalidation: 'skip',
      // EditAccountPassword.vue renders its own LCallout with support contact details,
      // so the global toast would be redundant.
      onError: (error) => {
        console.error(error)
      },
    }),

  updateDefaultAddress: () =>
    mutationOptions({
      mutationFn: async (address: PutDefaultAddress): Promise<{ success: boolean }> => {
        const client = await useClient()
        const { status, body } = await client.auth.updateDefaultCustomerAddress({ body: address })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to update default address')
      },
      // Orchestration re-validates cutoffs for every future order when the default
      // address changes (applyToFutureOrders: true) — delivery date/slot/pricing can
      // all shift server-side, so orders must be refetched alongside the address
      // itself (mirrors orderMutations.updateOrderDeliveryInfo's invalidation).
      invalidation: { awaited: [{ entity: 'address' }], background: [{ entity: 'order' }] },
    }),

  updateDefaultPaymentMethod: () =>
    mutationOptions({
      mutationFn: async ({
        paymentMethodId,
        provider,
      }: {
        paymentMethodId: string
        provider: string
      }): Promise<PaymentMethod> => {
        const client = await useClient()
        const { status, body } = await client.payment.updateDefaultPaymentMethod({
          body: { paymentMethodId, provider },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to update payment method')
      },
      invalidation: { awaited: [{ entity: 'payment' }] },
    }),
}
